<template>
  <div class="vivo">
    <!-- E1: registering a claim is its own page (/claims/new). It used to be a
         collapsible panel on the claims list, which meant the long form pushed
         the whole table down and the user lost their place in it. -->
    <div class="row between" style="margin-bottom: 14px">
      <div>
        <div class="pagetitle">{{ $t("claims.create.title") }}</div>
        <div class="crumb">
          {{ $t("common.company") }} / {{ $t("claims.title") }} / {{ $t("claimForm.crumbNew") }}
        </div>
      </div>
      <button class="btn ghost" type="button" data-test="claim-form-back" :disabled="submitting" @click="goList">
        {{ $t("claimForm.backToList") }}
      </button>
    </div>

    <div v-if="loading" class="state">{{ $t("claimForm.loading") }}</div>
    <div v-else-if="loadError" class="state" data-test="claim-form-load-error">
      {{ $t("claimForm.loadError") }}
    </div>

    <template v-else>
      <!-- Partial success: the claim IS saved but some files did not upload.
           We deliberately do NOT redirect here — bouncing to the list would
           hide the failures, and the user needs to know which files to re-add.
           The form is locked so the claim cannot be submitted twice. -->
      <div v-if="createdId" class="panel" data-test="claim-partial" style="margin-bottom: 14px">
        <div class="panel__body">
          <div class="callout">✅ {{ $t("claims.create.success", { id: createdId }) }}</div>
          <div v-if="attachSummary" class="sm mt">
            📎 {{ $t("claims.create.attach.uploaded", { n: attachSummary.uploaded }) }}
            <ul v-if="attachSummary.failed.length" class="xs" style="margin: 6px 0 0; padding-inline-start: 18px">
              <li v-for="(f, i) in attachSummary.failed" :key="i" class="err">
                {{ f.name }}: {{ $t("claims.create.attach.apiErrors." + f.errorKey) }}
              </li>
            </ul>
          </div>
          <div class="note info mt">{{ $t("claimForm.partialHint") }}</div>
          <div class="row mt" style="gap: 8px">
            <button class="btn primary" type="button" data-test="claim-goto-list" @click="goList">
              {{ $t("claimForm.goToList") }}
            </button>
          </div>
        </div>
      </div>

      <div v-else class="panel">
        <div class="panel__head">
          <h4>📝 {{ $t("claims.create.title") }}</h4>
        </div>
        <form class="panel__body" novalidate @submit.prevent="submitClaim">
          <div v-if="formError" class="note danger" style="margin-bottom: 10px" role="alert" data-test="claim-form-error">
            {{ formError }}
          </div>
          <div class="grid2">
            <label class="field">
              <span class="sm">{{ $t("claims.create.employee") }} *</span>
              <select v-model="form.employeeId" data-test="claim-employee" :disabled="submitting">
                <option value="">{{ $t("claims.create.employeePlaceholder") }}</option>
                <option v-for="e in employeeOptions" :key="e.id" :value="e.id">{{ e.name }}</option>
              </select>
              <span v-if="fieldErrors.employeeId" class="xs err">{{ fe("employeeId") }}</span>
            </label>
            <label class="field">
              <span class="sm">{{ $t("claims.create.amount") }} ({{ $t("claims.currency") }}) *</span>
              <input type="text" inputmode="decimal" data-test="claim-amount" v-model.trim="form.amount" :disabled="submitting" placeholder="0.00" />
              <span v-if="fieldErrors.amount" class="xs err">{{ fe("amount") }}</span>
            </label>
            <label class="field">
              <span class="sm">{{ $t("claims.create.serviceDate") }} *</span>
              <input type="date" data-test="claim-date" v-model="form.serviceDate" :max="today" :disabled="submitting" />
              <span v-if="fieldErrors.serviceDate" class="xs err">{{ fe("serviceDate") }}</span>
            </label>
            <label class="field">
              <span class="sm">{{ $t("claims.create.policy") }}</span>
              <select v-model="form.policyId" :disabled="submitting" data-test="claim-policy">
                <option value="">{{ $t("claims.create.policyDefault") }}</option>
                <option v-for="p in policyOptions" :key="p.id" :value="p.id">{{ p.label }}</option>
              </select>
              <span class="xs muted">{{ $t("claims.create.policyHint") }}</span>
            </label>
            <label class="field">
              <span class="sm">{{ $t("claims.create.provider") }} *</span>
              <input type="text" data-test="claim-provider" v-model.trim="form.provider" maxlength="200" :disabled="submitting" />
              <span v-if="fieldErrors.provider" class="xs err">{{ fe("provider") }}</span>
            </label>
          </div>
          <label class="field mt">
            <span class="sm">{{ $t("claims.create.note") }}</span>
            <textarea v-model.trim="form.note" rows="2" maxlength="1000" :disabled="submitting"></textarea>
            <span v-if="fieldErrors.note" class="xs err">{{ fe("note") }}</span>
          </label>
          <div class="field mt">
            <span class="sm">{{ $t("claims.create.attach.label") }}</span>
            <div class="row" style="gap: 8px; flex-wrap: wrap">
              <input
                ref="fileInput"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                :disabled="submitting"
                data-test="claim-files"
                @change="onFiles"
              />
              <select v-model="attachKind" :disabled="submitting" style="max-width: 170px">
                <option v-for="k in attachKinds" :key="k" :value="k">{{ $t("claims.create.attach.kinds." + k) }}</option>
              </select>
            </div>
            <span class="xs muted">{{ $t("claims.create.attach.hint", { n: maxFiles }) }}</span>
            <ul v-if="files.length" class="xs" style="margin: 4px 0 0; padding-inline-start: 18px">
              <li v-for="(f, i) in files" :key="i">
                {{ f.name }}
                <button type="button" class="btn ghost sm" :disabled="submitting" @click="removeFile(i)">✕</button>
              </li>
            </ul>
            <div v-for="(er, i) in fileErrors" :key="'e' + i" class="xs err">
              {{ er.name }}: {{ $t("claims.create.attach.errors." + er.key) }}
            </div>
          </div>
          <div class="divider"></div>
          <div class="row" style="gap: 8px">
            <button class="btn primary" type="submit" data-test="claim-submit" :disabled="submitting">
              {{ submitting ? $t("claims.create.submitting") : $t("claims.create.submit") }}
            </button>
            <button class="btn ghost" type="button" :disabled="submitting" @click="goList">
              {{ $t("claims.create.cancel") }}
            </button>
          </div>
        </form>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiPost, apiPostForm } from "../../api/vivo/http";
import { companyPath } from "../../api/vivo/session";
import {
  buildMap,
  validateClaimForm,
  buildClaimPayload,
  claimErrorKey,
  checkAttachments,
  uploadAttachments,
  ATTACH_KINDS,
  ATTACH_MAX_FILES,
  todayIso,
  blankClaimForm
} from "./claims-vm";

export default {
  name: "VivoClaimForm",
  data() {
    return {
      nameMap: {},
      policyRows: [],
      form: blankClaimForm(),
      fieldErrors: {},
      formError: "",
      files: [],
      fileErrors: [],
      attachKind: "invoice",
      attachKinds: ATTACH_KINDS,
      maxFiles: ATTACH_MAX_FILES,
      attachSummary: null,
      createdId: "",
      submitting: false,
      today: todayIso(),
      loading: true,
      loadError: false
    };
  },
  computed: {
    employeeOptions() {
      return Object.keys(this.nameMap).map((id) => ({ id, name: this.nameMap[id] }));
    },
    policyOptions() {
      return this.policyRows
        .filter((p) => !p.status || p.status === "active")
        .map((p) => ({
          id: String(p.id),
          label: p.classLabel ? p.number + " · " + p.classLabel : p.number
        }));
    }
  },
  async mounted() {
    // Its own page means its own dropdown data: the employee and policy lists
    // are no longer inherited from whatever the list screen happened to load.
    try {
      const [emps, policies] = await Promise.all([
        apiGet("/employees", { limit: 100 }),
        apiGet(companyPath("policies"), { limit: 100 })
      ]);
      this.nameMap = buildMap((emps && emps.data) || [], "fullName");
      this.policyRows = (policies && policies.data) || [];
    } catch (e) {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    goList() {
      if (this.$route.name !== "claims") this.$router.push({ name: "claims" });
    },
    fe(field) {
      return this.$t("claims.create.errors." + this.fieldErrors[field]);
    },
    onFiles(ev) {
      const picked = Array.prototype.slice.call((ev.target && ev.target.files) || []);
      const res = checkAttachments(picked, this.files.length);
      this.files = this.files.concat(res.accepted);
      // `errors`, NOT `rejected` — checkAttachments returns {accepted, errors}.
      // Reading the wrong key set fileErrors to undefined, and Vue 2's v-for
      // renders nothing for a non-iterable without warning, so a rejected file
      // (wrong type, empty, too big, over the 10-file cap) just vanished from
      // the list with no message at all.
      this.fileErrors = res.errors;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    removeFile(i) {
      this.files = this.files.slice(0, i).concat(this.files.slice(i + 1));
    },
    async submitClaim() {
      if (this.submitting || this.createdId) return;
      this.formError = "";
      this.fieldErrors = validateClaimForm(this.form, this.today);
      if (Object.keys(this.fieldErrors).length) {
        this.formError = this.$t("claims.create.fixErrors");
        return;
      }
      this.submitting = true;
      try {
        const created = await apiPost("/claims", buildClaimPayload(this.form));
        const id = String((created && created.id) || "");
        // Attachments are File objects held in this component, so they MUST be
        // uploaded before we navigate away — a route change destroys them.
        let summary = null;
        if (this.files.length && created && created.id != null) {
          summary = await uploadAttachments(
            created.id,
            this.files,
            this.attachKind,
            apiPostForm,
            (file, kind) => {
              const fd = new FormData();
              fd.append("file", file);
              fd.append("kind", kind);
              return fd;
            }
          );
        }
        if (summary && summary.failed.length) {
          // Stay put and show exactly which files failed.
          this.createdId = id;
          this.attachSummary = summary;
          return;
        }
        // "ولما احفظ البيانات تفتح الصفحة اللي فيها كل المطالبات"
        this.$router.push({
          name: "claims",
          query: { saved: id, uploaded: summary ? String(summary.uploaded) : undefined }
        });
      } catch (e) {
        this.formError = this.$t("claims.create.apiErrors." + claimErrorKey(e));
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field input,
.field select,
.field textarea {
  padding: 8px 10px;
  border: 1px solid var(--v-line-2);
  border-radius: 8px;
  font: inherit;
  background: #fff;
}
.err {
  color: var(--v-danger);
}
</style>
